import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  RadioGroup,
  Radio,
  VStack,
  HStack,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import httpInjectorService from '../../../../services/http-injector.service';
import { useParams } from 'react-router-dom';
import { FiFilter } from 'react-icons/fi';

const FilterDropdown = ({
  reqBody,
  setReqBody,
  dashboardData,
  setDashboardData,
}) => {
  const { id: projectId } = useParams();
  const [activeChip, setActiveChip] = useState(null);

  const [submenuData, setSubmenuData] = useState({
    assignee_id: [],
    sprint_id: [],
    epic_id: [],
    status: [],
    priority: ['highest', 'high', 'medium', 'low', 'lowest'], // example static data
    work_type: ['epic', 'task', 'subtask'],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    assignee_id: null,
    sprint_id: null,
    epic_id: null,
    status: null,
    priority: null,
    work_type: null,
  });

  const filterLabels = {
    assignee_id: 'ASSIGNEE',
    sprint_id: 'SPRINT',
    epic_id: 'EPIC',
  };

  const handleOptionSelect = (filterKey, value) => {
    // UI state (chip + radio)
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));

    setReqBody((prev) => {
      // SPECIAL CASE: status → send name instead of id
      if (filterKey === 'status') {
        const selectedStatus = submenuData.status.find(
          (st) => String(st.id) === String(value),
        );

        return {
          ...prev,
          status: selectedStatus?.name ? [selectedStatus.name] : [],
        };
      }

      // DEFAULT: send id/value as array
      return {
        ...prev,
        [filterKey]: value ? [value] : [],
      };
    });

    setActiveChip(null);
  };

  const clearFilter = (filterKey) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: null,
    }));

    setReqBody((prev) => ({
      ...prev,
      [filterKey]: [],
    }));
  };

  useEffect(() => {
    const id = Number(projectId);

    const fetchAll = async () => {
      try {
        const [sprintRes, assigneeRes, statusRes, epicRes] = await Promise.all([
          httpInjectorService.boardFilterSprints(id),
          httpInjectorService.getProjectAssignedEmployeelist(id),
          httpInjectorService.ListBoardStatus(id),
          httpInjectorService.boardFilterEpics(id),
          // add priority/work_type calls if available
        ]);

        setSubmenuData({
          ...submenuData,
          sprint_id:
            sprintRes?.data?.active_sprints?.map((s) => ({
              id: s.sprint_id,
              sprint_name: s.sprint_name,
              status: s.status,
            })) || [],
          assignee_id:
            assigneeRes?.data?.map((u) => ({
              id: u.user_id,
              username: u.username,
              email: u.email,
            })) || [],
          status:
            statusRes?.data?.map((st) => ({
              id: st.id,
              name: st.status_name,
            })) || [],
          epic_id:
            epicRes?.data?.map((e) => ({
              id: e.epic_id,
              name: e.epic_name,
            })) || [],
        });
      } catch (err) {
        toast.error('Error fetching filters');
      }
    };

    fetchAll();
  }, [projectId]);

  return (
    <Box mb={4}>
      <HStack spacing={3} align="center" wrap="wrap">
        {/* FILTER MENU */}
        <Menu >
          <MenuButton
            as={Button}
            variant="outline"
            size="md"
            leftIcon={<FiFilter />}
          >
            Filters
          </MenuButton>

          <MenuList>
            {Object.keys(submenuData).map((filterKey) => (
              <MenuItem
                key={filterKey}
                onClick={() => {
                  // ensure chip appears even before selection
                  if (!selectedFilters[filterKey]) {
                    setSelectedFilters((prev) => ({
                      ...prev,
                      [filterKey]: '', // placeholder until user picks
                    }));
                  }
                  setActiveChip(filterKey);
                }}
              >
                {filterLabels[filterKey] ||
                  filterKey.replace(/_/g, ' ').toUpperCase()}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* SELECTED FILTER CHIPS */}
        {Object.entries(selectedFilters).map(([filterKey, value]) => {
          if (value === null) return null;

          const selectedOption = submenuData[filterKey]?.find(
            (opt) => String(opt.id ?? opt) === String(value),
          );

          const label =
            selectedOption?.name ||
            selectedOption?.username ||
            selectedOption?.sprint_name ||
            selectedOption ||
            value;

          return (
            <Popover
              key={filterKey}
              isOpen={activeChip === filterKey}
              onClose={() => setActiveChip(null)}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Tag
                  size="md"
                  colorScheme="blue"
                  cursor="pointer"
                  onClick={() => setActiveChip(filterKey)}
                >
                  <TagLabel>
                    {filterLabels[filterKey] || filterKey.replace(/_/g, ' ')}:{' '}
                    {label}
                  </TagLabel>
                  <TagCloseButton
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFilter(filterKey);
                    }}
                  />
                </Tag>
              </PopoverTrigger>

              <PopoverContent>
                <PopoverBody>
                  <Text fontSize="sm" mb={2} fontWeight="bold">
                    Select{' '}
                    {filterLabels[filterKey] || filterKey.replace(/_/g, ' ')}
                  </Text>

                  <RadioGroup
                    value={String(selectedFilters[filterKey] ?? '')}
                    onChange={(val) => handleOptionSelect(filterKey, val)}
                  >
                    <VStack align="start">
                      {submenuData[filterKey]?.map((option) => (
                        <Radio
                          key={option.id || option}
                          value={String(option.id || option)}
                        >
                          {option.name ||
                            option.username ||
                            option.sprint_name ||
                            option}
                        </Radio>
                      ))}
                    </VStack>
                  </RadioGroup>

                  {/* <Button onClick={()=> console.log(submenuData, "subemnuData")}>printer</Button> */}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          );
        })}
      </HStack>
    </Box>
  );
};

export default FilterDropdown;
